// AIA A101-2017 — Owner–Contractor Agreement (Stipulated Sum), incorporating
// A201-2017 General Conditions (copyright of the AIA; referenced, not reproduced).

import type { AssembledContract, Block, ContractPart, ProjectBrief } from "./types";
import { amountToWords } from "./amountToWords";

const D = "________________";
const or = (v?: string, f = D) => (v && v.trim() ? v : f);
// Number.isFinite, not global isFinite: a JSON round-trip turns NaN amounts into
// null, and isFinite(null) is true — which would crash on .toLocaleString().
const money = (m?: { amount: number; currency: string }) =>
  !m || !Number.isFinite(m.amount) ? D : `${m.currency} ${m.amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const vatTreatment = (b: ProjectBrief) =>
  b.priceVat === "inclusive" ? "inclusive of Value Added Tax (VAT)" : "exclusive of Value Added Tax (VAT)";

export function assembleAia(b: ProjectBrief): AssembledContract {
  const agreement: Block[] = [
    { type: "heading", text: "Standard Form of Agreement Between Owner and Contractor", level: 1 },
    { type: "paragraph", text: "AIA Document A101-2017 — where the basis of payment is a Stipulated Sum." },
    { type: "paragraph", text: `AGREEMENT made as of ${or(b.agreementDate, "the ____ day of ____________ 20__")}.` },
    { type: "paragraph", text: `BETWEEN the Owner: ${or(b.employer.name)}, ${or(b.employer.address)}` },
    { type: "paragraph", text: `and the Contractor: ${or(b.contractor.name)}, ${or(b.contractor.address)}` },
    { type: "paragraph", text: `for the Project: ${or(b.projectName)} — ${or(b.worksDescription)}, at ${or(b.projectLocation)}. The Architect: ${or(b.engineer?.name)}.` },
    { type: "heading", text: "Article 1 — The Contract Documents", level: 2 },
    { type: "paragraph", text: "The Contract Documents consist of this Agreement, the Conditions of the Contract (AIA A201-2017 General Conditions), Drawings, Specifications, Addenda, and Modifications." },
    { type: "heading", text: "Article 3 — Commencement and Substantial Completion", level: 2 },
    { type: "clause", text: `§ 3.1  Date of commencement: ${or(b.commencementDate, "the date of this Agreement")}.` },
    { type: "clause", text: `§ 3.3  Substantial Completion not later than ${or(b.timeForCompletion)} from the date of commencement.` },
    ...(b.delayDamagesPerDay ? [{ type: "clause", text: `§ 3.3.2  Liquidated damages of ${money(b.delayDamagesPerDay)} per day of delay.` } as Block] : []),
    { type: "heading", text: "Article 4 — Contract Sum", level: 2 },
    { type: "clause", text: `§ 4.1  The Owner shall pay the Contractor the Contract Sum of ${money(b.contractPrice)}${amountToWords(b.contractPrice) ? ` (${amountToWords(b.contractPrice)})` : ""}, ${vatTreatment(b)}.` },
    { type: "heading", text: "Article 5 — Payments", level: 2 },
    ...(b.retentionPercent != null ? [{ type: "clause", text: `§ 5.1.7  Retainage of ${b.retentionPercent}% withheld from each progress payment.` } as Block] : []),
    { type: "heading", text: "Article 8 — Miscellaneous", level: 2 },
    { type: "clause", text: `§ 8.2  Binding dispute resolution: ${or(b.disputeResolution, "Arbitration per § 15.4 of A201-2017")}.` },
    { type: "clause", text: `§ 8.3  Governing law: ${or(b.governingLaw)}.` },
    {
      type: "signblock",
      parties: [
        { role: "Owner", name: b.employer.representative, title: b.employer.representativeTitle },
        { role: "Contractor", name: b.contractor.representative, title: b.contractor.representativeTitle },
      ],
    },
  ];

  const parts: ContractPart[] = [
    { id: "a101", name: "A101 Owner–Contractor Agreement", cover: { kicker: "Section 1", title: "Owner–Contractor Agreement", subtitle: b.projectName, meta: [{ label: "Contract Sum", value: money(b.contractPrice) }] }, blocks: agreement },
    {
      id: "drawings",
      name: "Exhibit — Drawing List",
      cover: { kicker: "Exhibit A", title: "Drawing List", subtitle: b.projectName },
      blocks: [
        { type: "heading", text: "Exhibit A — Drawing List", level: 1 },
        { type: "paragraph", text: `The following ${b.drawings.length} Drawings are Contract Documents under Article 1.` },
        { type: "table", columns: ["#", "Sheet No.", "Title", "Rev.", "Discipline"], rows: b.drawings.map((d, i) => [String(i + 1), d.number || "—", d.title || "—", d.revision || "—", d.discipline || "—"]) },
      ],
    },
  ];

  return {
    title: `${or(b.projectName, "Construction Contract")}`,
    standardLabel: "AIA Document A101-2017 (incorporating A201-2017 General Conditions)",
    masterCover: { kicker: "Contract Documents", title: or(b.projectName, "Construction Contract"), subtitle: b.worksDescription, meta: [{ label: "Owner", value: or(b.employer.name) }, { label: "Contractor", value: or(b.contractor.name) }, { label: "Contract Sum", value: money(b.contractPrice) }] },
    parts,
    notice: "Reference draft of AIA A101-2017 fill-points. The official AIA A101/A201 are copyright of the AIA and must be executed on licensed forms. Not legal advice.",
  };
}
